// Granularity Constants
// 2160 points = 1 point per 5 seconds for 3 hours
export const DESIRED_BUCKETS = 256;

// Time Constants
export const MIN_GRANULARITY_SECONDS = 5;
export const ONE_MINUTE = 60;
export const ONE_HOUR = 3600;

export const MAX_RANGE_SECONDS = 25000; // 5000 data points * 5 seconds (~7 hours)
// Period Types
//
// Open-ended `{number}{unit}` shape rather than a closed enum, so the server
// can introduce new ranges (e.g. "2h", "30min", "1q", "10y") without a
// frontend type bump. TypeScript still validates the format via template
// literal; switch-statement consumers handle the specific values they care
// about and fall through to a default for unknown periods.
//
// Wire format unit spellings (chosen to be unambiguous in URLs, logs, and
// case-folding pipelines):
//   min  — minute (e.g. "5min", "15min", "30min")
//   h    — hour   (e.g. "1h", "3h", "4h", "6h")
//   d    — day    (e.g. "1d")
//   w    — week   (e.g. "1w", "2w")
//   mo   — month  (e.g. "1mo", "3mo", "6mo")
//   q    — quarter (e.g. "1q", "2q") — reserved; not yet emitted
//   y    — year   (e.g. "1y", "2y", "5y")
//
// Plus the semantic non-duration tokens: "all", "live", "game", and "custom"
// (an absolute window supplied by product-metadata's default_chart_periods file,
// see utils/defaultChartPeriods.ts).
type DurationUnit = 'min' | 'h' | 'd' | 'w' | 'mo' | 'q' | 'y';
type SemanticPeriod = 'all' | 'live' | 'game' | 'custom';
export type PERIOD_TYPE = `${number}${DurationUnit}` | SemanticPeriod;

// Histogram Metric Types
export type HistogramMetric = 'VOL' | 'OI' | null;

// Period Options
export const PERIOD_OPTIONS: Array<PERIOD_TYPE> = ['live', '1h', '3h', '6h', '1d', '1w', '1mo', '2y', '5y', 'all'];

/**
 * Timeframe options for crypto price charts, by how the market is framed.
 *
 * Shared by the desktop chart and the embed so the two cannot drift, which is how they ended up both
 * hard-coding `1h/3h/1d/1mo` for everything. `activePeriodToIndexFrequency` already serves `15min` and `4h`.
 *
 * Keyed on the ticker rather than `series.frequency`: what the product calls a daily (KXBTCD, KXETHD) is an
 * hourly-cadence series presented differently, so `frequency` reads `hourly` for both and cannot tell them
 * apart. The ticker suffix is the reliable signal.
 */
export const CRYPTO_HOURLY_PERIODS: Array<PERIOD_TYPE> = ['15min', '1h', '4h', '1d'];
export const CRYPTO_DAILY_PERIODS: Array<PERIOD_TYPE> = ['1h', '4h', '1d', '1w', '1mo'];
// Weekly (`…MAXW`) and annual (`…MAXY`) series keep the ladder every crypto chart used before the
// cadence-based ones were introduced. A 15-minute window says nothing about a market resolving once a year,
// and the short ladders drop the month view these rely on.
export const CRYPTO_LONG_PERIODS: Array<PERIOD_TYPE> = ['1h', '3h', '1d', '1mo'];

// Ordered longest-suffix-first so `MAXW`/`MAXY` are classified before the bare trailing-`D` check. A plain
// asset ticker (KXBTC, KXETH) matches nothing and takes the hourly ladder, which is the common case.
const CRYPTO_LADDER_BY_TICKER_SUFFIX: Array<[RegExp, Array<PERIOD_TYPE>]> = [
    [/MAXW$/, CRYPTO_LONG_PERIODS],
    [/MAXY$/, CRYPTO_LONG_PERIODS],
    [/15M$/, CRYPTO_HOURLY_PERIODS],
    [/D$/, CRYPTO_DAILY_PERIODS],
];

export function cryptoPeriodsForSeries(seriesTicker: string | undefined): Array<PERIOD_TYPE> {
    const ticker = (seriesTicker ?? '').toUpperCase();
    return CRYPTO_LADDER_BY_TICKER_SUFFIX.find(([pattern]) => pattern.test(ticker))?.[1] ?? CRYPTO_HOURLY_PERIODS;
}

export const MAX_DATA_POINTS = 5000; // Maximum number of data points to be fetched defined in the API
